import { useEffect, useMemo, useRef, useState } from 'react';

import { AutoComplete, Form, Input, InputRef } from '~src/components/display';
import { useLocalization, useToastMessage } from '~src/hooks';
import { useBbrUnit } from '~src/hooks/services/useBbrUnit';
import { useDawaAddressSuggestions } from '~src/hooks/services/useDawa';
import {
  Address,
  DAWAAddress,
  DAWAAutocompleteSuggestion,
  mapResidenceToResidenceInput,
  Residence,
  ResidenceInput,
} from '~src/types';
import { createAddressSelector, toAddressFromDAWAAddress } from '~src/utilities/addressHelper';
import { deepNullToUndefined } from '~src/utilities/convert';

type AddressSearchNewProps = {
  className?: string;
  existingAddressSuggestion?: string;
  onSelect: (residence?: ResidenceInput) => void;
};

export const AddressSearchNew = ({ className, existingAddressSuggestion, onSelect }: AddressSearchNewProps) => {
  const translate = useLocalization();
  const { showMessage } = useToastMessage();

  const inputRef = useRef<InputRef>(null);

  const [search, setSearch] = useState(existingAddressSuggestion);
  const [selectedAddress, setSelectedAddress] = useState<DAWAAddress>();

  const selector = useMemo(createAddressSelector, []);

  useEffect(() => selector.cancel, [selector]);

  const { suggestions } = useDawaAddressSuggestions(search ?? '');

  const suggestedAddress = toAddressFromDAWAAddress(selectedAddress);
  const {
    bbrUnit,
    noBbrData,
    isFetching: isFetchingBbrData,
  } = useBbrUnit(suggestedAddress, suggestedAddress?.id?.toString());

  // Street and house hits refine the search; addresses are hydrated before their BBR data is fetched.
  const resolveSuggestion = async (suggestion: DAWAAutocompleteSuggestion) => {
    const selection = await selector.select(suggestion);

    if (selection.status === 'refine') {
      setSearch(selection.search);
    }

    if (selection.status === 'resolved') {
      setSelectedAddress(selection.address);
      inputRef?.current?.blur();
    }

    if (selection.status === 'not_found') {
      showMessage({ message: translate.ADDRESS_NOT_FOUND, type: 'warning' });
    }

    if (selection.status === 'failed') {
      showMessage({ message: translate.ADDRESS_LOOKUP_FAILED, type: 'error' });
    }
  };

  useEffect(
    function focusOnPageLoad() {
      if (inputRef.current?.focus) {
        inputRef.current.focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputRef.current]
  );

  useEffect(
    function synchronizeResidenceFromUnit() {
      if (noBbrData) {
        showMessage({ message: translate.NO_BBR_DATA_TO_FETCH, type: 'warning' });
      }

      const unitAddress = deepNullToUndefined(suggestedAddress);

      if (!isFetchingBbrData && !bbrUnit) {
        onSelect({
          area: 0,
          builtYear: 1900,
          areaHeated: 0,
          floors: 1,
          waterConsumption: 'medium',
          energyExpenditure: 0,
          type: 'villa',
          heatDistribution: 'radiator',
          primaryHeating: {
            fuelType: 'gas',
            annualUsage: 0,
          },
          address: toAddress(unitAddress),
        });
        return;
      }

      inputRef?.current?.blur();

      const newResidence = mapBBRUnitToResidence(bbrUnit, unitAddress);

      if (!bbrUnit) {
        return;
      }

      onSelect(mapResidenceToResidenceInput(newResidence));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAddress?.id, !!bbrUnit]
  );

  useEffect(
    function autoSelectSingleAddressSuggestion() {
      if (suggestions?.length !== 1) {
        return;
      }

      if (suggestions[0].type === 'vejnavn') {
        return;
      }

      resolveSuggestion(suggestions[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestions]
  );

  const handleChange = (value: string) => {
    if (value === search) {
      return;
    }

    selector.cancel();
    setSearch(value);
  };

  const handleSelectSuggestion = (suggestionId: string) => {
    const address = suggestions[parseInt(suggestionId, 10)];

    if (!address) {
      return;
    }

    setSearch(address.forslagstekst);

    resolveSuggestion(address);
  };

  return (
    <Form.Item className={className} label={translate.SEARCH_FOR_ADDRESS}>
      <AutoComplete<string>
        options={suggestions?.map((suggestion: { forslagstekst: string }, index: number) => ({
          label: suggestion.forslagstekst,
          value: index.toString(),
        }))}
        onChange={handleChange}
        onSelect={handleSelectSuggestion}
        value={search}
      >
        <Input.Search ref={inputRef} />
      </AutoComplete>
    </Form.Item>
  );
};

const mapBBRUnitToResidence = (
  bbrUnit: ReturnType<typeof useBbrUnit>['bbrUnit'],
  address?: Partial<Omit<Address, 'id'>>
): Residence =>
  // Ignore use of partial type returned from legacy shared library
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ({
    ...bbrUnit,
    address: toAddress(address),
  });

const toAddress = (address?: Omit<Partial<Address>, 'id'>) => ({
  city: address?.city ?? '',
  houseNumber: address?.houseNumber ?? '',
  postalCode: address?.postalCode ?? '',
  street: address?.street ?? '',
  floor: address?.floor,
  door: address?.door,
});
